<template>
  <div class="payment-link-button">
    <!-- Main button + popup menu (replaces SplitButton) -->
    <template v-if="hasPaymentAccount">
      <Button
        data-testid="subaccounts-link-button-main"
        :label="buttonLabel"
        :icon="buttonIcon"
        :loading="isLoading"
        :disabled="isDisabled"
        class="p-button-sm"
        :class="buttonClass"
        @click="handleMainClick"
      />
      <Button
        data-testid="subaccounts-link-button-menu-toggle"
        icon="pi pi-chevron-down"
        class="p-button-sm"
        :class="buttonClass"
        :disabled="isDisabled"
        aria-haspopup="true"
        aria-controls="subaccounts-link-menu"
        @click="toggleMenu"
      />
      <Menu
        id="subaccounts-link-menu"
        ref="menuRef"
        :model="menuItems"
        :popup="true"
      />
    </template>

    <!-- Fallback button (no payment account configured) -->
    <Button
      v-else
      data-testid="subaccounts-link-configure-button"
      :label="t('Configurar Pagamentos')"
      icon="pi pi-cog"
      class="p-button-sm p-button-secondary"
      v-tooltip.top="t('Configure uma conta de pagamento para gerar links')"
      @click="handleConfigurePayments"
    />

    <!-- Payment Link Modal -->
    <modal
      elem-id="modal_franchises_subaccounts_link"
      :show="showLinkDialog"
      :title="t('Link de Pagamento')"
      size="small"
      :closeLabel="t('Cancelar')"
      :saveLabel="activeLink ? t('Desativar Link') : t('Gerar Link')"
      :saveIcon="activeLink ? 'pi pi-times' : 'pi pi-link'"
      :saveFn="activeLink ? handleDeactivateLink : handleCreateLink"
      :saveDisabledFn="() => isCreating || isDeactivating"
      @close="showLinkDialog = false"
      @dismiss="showLinkDialog = false"
    >
      <div v-if="activeLink" class="link-info">
        <div class="link-status">
          <Tag
            :value="getLinkStatusConfig(activeLink.status).label"
            :severity="getLinkStatusConfig(activeLink.status).severity"
          />
          <span class="link-value">{{ formatValue(activeLink.value) }}</span>
        </div>

        <div class="link-url-container">
          <input-text
            data-testid="subaccounts-link-url-input"
            v-model="activeLinkUrl"
            elem-id="subaccounts-link-url"
            :readonly="true"
            class="w-full link-url"
          />
          <Button
            data-testid="subaccounts-link-copy-button"
            icon="pi pi-copy"
            class="p-button-secondary"
            v-tooltip.top="t('Copiar')"
            @click="handleCopyLink"
          />
        </div>

        <div class="link-actions">
          <Button
            data-testid="subaccounts-link-open-button"
            :label="t('Abrir Link')"
            icon="pi pi-external-link"
            class="p-button-outlined"
            @click="handleOpenLink"
          />
          <Button
            data-testid="subaccounts-link-whatsapp-button"
            :label="t('Enviar por WhatsApp')"
            icon="pi pi-whatsapp"
            class="p-button-success"
            @click="handleSendWhatsApp"
          />
        </div>
      </div>

      <div v-else class="create-link-form">
        <div class="field">
          <select-list
            data-testid="subaccounts-link-payment-method-select"
            v-model="newLinkOptions.payment_method"
            elem-id="subaccounts-link-payment-method"
            :label="t('Método de Pagamento')"
            :itens-select="paymentMethodOptions"
          />
        </div>

        <div class="field">
          <select-list
            data-testid="subaccounts-link-charge-type-select"
            v-model="newLinkOptions.charge_type"
            elem-id="subaccounts-link-charge-type"
            :label="t('Tipo de Cobrança')"
            :itens-select="chargeTypeOptions"
          />
        </div>

        <div v-if="newLinkOptions.charge_type === 'installment'" class="field">
          <input-number
            data-testid="subaccounts-link-installments-input"
            v-model="newLinkOptions.installments"
            elem-id="subaccounts-link-installments"
            :label="t('Parcelas')"
            :min="2"
            :max="12"
          />
        </div>

        <div class="field">
          <input-number
            data-testid="subaccounts-link-due-days-input"
            v-model="newLinkOptions.due_days"
            elem-id="subaccounts-link-due-days"
            :label="t('Dias para Vencimento')"
            :min="1"
            :max="90"
          />
        </div>

        <div class="field-checkbox">
          <input-toggle
            data-testid="subaccounts-link-notify-customer-toggle"
            v-model="notifyCustomer"
            elem-id="subaccounts-link-notify-customer"
            :label="t('Notificar cliente')"
          />
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { usePaymentLinks } from '../../../composables/usePaymentLinks';
import { useFranchisesSubaccounts } from '../../../composables/useFranchisesSubaccounts';
import { usePaymentProviders } from '../../../composables/usePaymentProviders';
import { usePaymentInvoices } from '../../../composables/usePaymentInvoices';
import { useFiltro } from '../../../composables/useFiltro';

type Id = number | string;

interface Props {
  compromissoId: Id;
  clienteNome?: string;
  clienteTelefone?: string;
  valorTotal?: number;
  valorRestante?: number;
  variant?: 'default' | 'compact' | 'icon-only';
}

const props = withDefaults(defineProps<Props>(), {
  clienteNome: '',
  clienteTelefone: '',
  valorTotal: 0,
  valorRestante: 0,
  variant: 'default'
});

interface PaymentLink {
  id: Id;
  url: string;
  status: string;
  value: number;
}

interface PaymentAccount {
  id: Id;
  provider_code?: string;
}

interface PaymentMethodOption {
  value: string;
  label: string;
}

interface SelectOption {
  ID: string;
  Name: string;
}

interface MenuItem {
  label?: string;
  icon?: string;
  command?: () => void;
  separator?: boolean;
}

interface NewLinkOptions {
  payment_method: string;
  charge_type: 'single' | 'installment';
  installments: number;
  due_days: number;
  notify_customer: boolean;
}

interface MenuInstance {
  toggle: (event: Event) => void;
}

const emit = defineEmits<{
  'link-created': [link: PaymentLink];
  'link-deactivated': [link: PaymentLink];
  'invoice-created': [invoice: unknown];
}>();

const { t } = useI18n();
const {
  createPaymentLink,
  getActiveLink,
  deactivateLink,
  copyLinkToClipboard,
  openLinkInNewTab,
  getLinkStatusConfig,
  formatValue
} = usePaymentLinks();
const { getByFranchise: getPrimaryAccountForFranchise } = useFranchisesSubaccounts();
const { getPaymentMethods } = usePaymentProviders();
const { hasAuthorizedInvoice, createInvoice, isFiscalConfigured } = usePaymentInvoices();
const { selectedFranchises } = useFiltro();

// State
const isLoading = ref<boolean>(false);
const isCreating = ref<boolean>(false);
const isDeactivating = ref<boolean>(false);
const hasPaymentAccount = ref<boolean>(false);
const hasInvoice = ref<boolean>(false);
const showLinkDialog = ref<boolean>(false);
const activeLink = ref<PaymentLink | null>(null);
const paymentAccount = ref<PaymentAccount | null>(null);
const menuRef = ref<MenuInstance | null>(null);

const newLinkOptions = ref<NewLinkOptions>({
  payment_method: 'any',
  charge_type: 'single',
  installments: 2,
  due_days: 10,
  notify_customer: true
});

// Computed
const buttonLabel = computed<string>(() => {
  if (props.variant === 'icon-only') return '';
  if (activeLink.value) return t('Ver Link');
  return t('Gerar Link');
});

const buttonIcon = computed<string>(() => {
  if (activeLink.value) return 'pi pi-link';
  return 'pi pi-plus';
});

const buttonClass = computed<string>(() => {
  if (activeLink.value) return 'p-button-info';
  return 'p-button-success';
});

const isDisabled = computed<boolean>(() => {
  return props.valorRestante <= 0;
});

const activeLinkUrl = computed<string>({
  get: () => activeLink.value?.url ?? '',
  set: () => { /* readonly */ }
});

const notifyCustomer = computed<boolean>({
  get: () => newLinkOptions.value.notify_customer,
  set: (val: boolean) => { newLinkOptions.value.notify_customer = val; }
});

const paymentMethodOptions = computed<SelectOption[]>(() => {
  const code = paymentAccount.value?.provider_code || 'asaas';
  const methods = getPaymentMethods(code) as PaymentMethodOption[];
  return methods.map(m => ({ ID: m.value, Name: m.label }));
});

const chargeTypeOptions = computed<SelectOption[]>(() => [
  { ID: 'single', Name: t('Pagamento Único') },
  { ID: 'installment', Name: t('Parcelado') }
]);

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [];

  if (activeLink.value) {
    items.push(
      {
        label: t('Copiar Link'),
        icon: 'pi pi-copy',
        command: handleCopyLink
      },
      {
        label: t('Abrir Link'),
        icon: 'pi pi-external-link',
        command: handleOpenLink
      },
      {
        label: t('Enviar WhatsApp'),
        icon: 'pi pi-whatsapp',
        command: handleSendWhatsApp
      },
      { separator: true },
      {
        label: t('Desativar Link'),
        icon: 'pi pi-times',
        command: () => { showLinkDialog.value = true; }
      }
    );
  } else {
    items.push(
      {
        label: t('Gerar Link PIX'),
        icon: 'pi pi-qrcode',
        command: () => handleQuickCreate('pix')
      },
      {
        label: t('Gerar Link Boleto'),
        icon: 'pi pi-file',
        command: () => handleQuickCreate('boleto')
      },
      {
        label: t('Gerar Link Cartão'),
        icon: 'pi pi-credit-card',
        command: () => handleQuickCreate('credit_card')
      }
    );
  }

  // Add invoice option if configured
  if (paymentAccount.value && !hasInvoice.value) {
    items.push(
      { separator: true },
      {
        label: t('Gerar Nota Fiscal'),
        icon: 'pi pi-file',
        command: handleCreateInvoice
      }
    );
  }

  return items;
});

// Methods
const toggleMenu = (event: Event): void => {
  menuRef.value?.toggle(event);
};

const loadData = async (): Promise<void> => {
  log('FranchisesSubaccountsLinkButton', 'loadData', 'Loading data for compromisso', props.compromissoId);
  isLoading.value = true;

  try {
    // Use the user's currently filtered franchise from useFiltro (global filter).
    // Same pattern as PDVRegistrarEntregaModal — first selected franchise.
    const franchises = selectedFranchises.value as Array<{ id: number | string; name: string }>;
    const franchiseId = franchises?.[0]?.id;
    if (!franchiseId) {
      log('FranchisesSubaccountsLinkButton', 'loadData', 'No franchise selected');
      return;
    }

    // Check for payment account
    paymentAccount.value = (await getPrimaryAccountForFranchise(Number(franchiseId))) as PaymentAccount | null;
    hasPaymentAccount.value = !!paymentAccount.value;

    if (hasPaymentAccount.value) {
      // Check for active link
      activeLink.value = (await getActiveLink(Number(props.compromissoId))) as PaymentLink | null;

      // Check for invoice
      hasInvoice.value = await hasAuthorizedInvoice(Number(props.compromissoId));
    }

    log('FranchisesSubaccountsLinkButton', 'loadData', 'Data loaded', {
      hasPaymentAccount: hasPaymentAccount.value,
      hasActiveLink: !!activeLink.value,
      hasInvoice: hasInvoice.value
    });
  } catch (err) {
    log('FranchisesSubaccountsLinkButton', 'loadData', 'Error', err);
  } finally {
    isLoading.value = false;
  }
};

const handleMainClick = (): void => {
  showLinkDialog.value = true;
};

const handleConfigurePayments = (): void => {
  // Navigate to payments configuration
  window.location.href = '/subaccounts/franchises';
};

const handleQuickCreate = async (paymentMethod: string): Promise<void> => {
  log('FranchisesSubaccountsLinkButton', 'handleQuickCreate', 'Quick create with method', paymentMethod);
  isCreating.value = true;

  try {
    const link = (await createPaymentLink(Number(props.compromissoId), {
      payment_method: paymentMethod,
      charge_type: 'single',
      due_days: 10,
      notify_customer: true
    })) as unknown as PaymentLink;

    activeLink.value = link;
    emit('link-created', link);

    eventBus.emit('globalMessage.success', t('Link de pagamento criado'));

    // Show dialog with link
    showLinkDialog.value = true;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('FranchisesSubaccountsLinkButton', 'handleQuickCreate', 'Error', err);
    eventBus.emit('globalMessage.error', message || t('Erro ao criar link'));
  } finally {
    isCreating.value = false;
  }
};

const handleCreateLink = async (): Promise<void> => {
  log('FranchisesSubaccountsLinkButton', 'handleCreateLink', 'Creating link', newLinkOptions.value);
  isCreating.value = true;

  try {
    const link = (await createPaymentLink(Number(props.compromissoId), newLinkOptions.value)) as unknown as PaymentLink;
    activeLink.value = link;
    emit('link-created', link);

    eventBus.emit('globalMessage.success', t('Link de pagamento criado'));
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('FranchisesSubaccountsLinkButton', 'handleCreateLink', 'Error', err);
    eventBus.emit('globalMessage.error', message || t('Erro ao criar link'));
  } finally {
    isCreating.value = false;
  }
};

const handleDeactivateLink = async (): Promise<void> => {
  if (!activeLink.value) return;

  log('FranchisesSubaccountsLinkButton', 'handleDeactivateLink', 'Deactivating link', activeLink.value.id);
  isDeactivating.value = true;

  try {
    await deactivateLink(Number(activeLink.value.id));
    emit('link-deactivated', activeLink.value);
    activeLink.value = null;
    showLinkDialog.value = false;

    eventBus.emit('globalMessage.success', t('Link desativado'));
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('FranchisesSubaccountsLinkButton', 'handleDeactivateLink', 'Error', err);
    eventBus.emit('globalMessage.error', message || t('Erro ao desativar link'));
  } finally {
    isDeactivating.value = false;
  }
};

const handleCopyLink = async (): Promise<void> => {
  if (!activeLink.value?.url) return;

  const success = await copyLinkToClipboard(activeLink.value.url);
  if (success) {
    eventBus.emit('globalMessage.success', t('Link copiado para a área de transferência'));
  }
};

const handleOpenLink = (): void => {
  if (!activeLink.value?.url) return;
  openLinkInNewTab(activeLink.value.url);
};

const handleSendWhatsApp = (): void => {
  if (!activeLink.value?.url) return;

  const phone = props.clienteTelefone?.replace(/\D/g, '') || '';
  const message = t('Olá! Segue o link para pagamento: {url}', { url: activeLink.value.url });
  const encodedMessage = encodeURIComponent(message);
  // api.whatsapp.com/send direto — o redirect do wa.me corrompe emojis no ?text.
  const whatsappUrl = phone
    ? `https://api.whatsapp.com/send/?phone=55${phone}&text=${encodedMessage}`
    : `https://api.whatsapp.com/send/?text=${encodedMessage}`;

  window.open(whatsappUrl, '_blank');
};

const handleCreateInvoice = async (): Promise<void> => {
  log('FranchisesSubaccountsLinkButton', 'handleCreateInvoice', 'Creating invoice');

  // Check if fiscal is configured for the currently filtered franchise.
  const franchises = selectedFranchises.value as Array<{ id: number | string; name: string }>;
  const franchiseId = franchises?.[0]?.id;
  if (!franchiseId) {
    eventBus.emit('globalMessage.warning', t('Selecione uma franquia antes de emitir notas'));
    return;
  }
  const configured = await isFiscalConfigured(Number(franchiseId));
  if (!configured) {
    eventBus.emit('globalMessage.warning', t('Configure os dados fiscais antes de emitir notas'));
    return;
  }

  // The manual-emit route (/subaccounts/invoices/criar) requires a full
  // Invoice_EmitInput (customer, amounts, dates) that this compromisso button
  // does not have. The previous compromisso-only call hit a non-existent route.
  // Emitting from a compromisso is not yet supported here — see W4 notes.
  log('FranchisesSubaccountsLinkButton', 'handleCreateInvoice', 'Compromisso-emit not supported by manual-emit route');
  eventBus.emit('globalMessage.warning', t('Emissão de nota fiscal a partir do compromisso ainda não está disponível'));
};

// Watch for compromissoId changes
watch(() => props.compromissoId, () => {
  loadData();
});

// Lifecycle
onMounted(() => {
  loadData();
});
</script>

<style scoped>
.payment-link-button {
  display: inline-flex;
  gap: 0.25rem;
}

.link-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.link-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.link-value {
  font-size: 1.25rem;
  font-weight: 600;
}

.link-url-container {
  display: flex;
  gap: 0.5rem;
}

.link-url {
  flex: 1;
  font-family: monospace;
  font-size: 0.875rem;
}

.link-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.create-link-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.w-full {
  width: 100%;
}
</style>
